import { StatusColor } from '../../common/enums.js';
import {
  activeMsg,
  closedMsg,
  contractsMsg,
  devicesMsg,
  inDeliveryMsg,
  internetSubscriptionsMsg,
  servicesMsg,
  t,
  temporarilyClosedMsg,
  unknownMsg,
  voiceSubscriptionsMsg,
} from '../../common/i18n/index.js';
import { isEppDeviceSubscriptionExpiring } from '../EoeSubscriptionDetails/eppSubscriptionUtils.js';
import { Status } from '../Status/Status.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary';
import type { SubscriptionStatus } from '../../common/types/subscription.js';
import type { Subscription, SubscriptionAction, SubscriptionStatusType } from '../../generated/api/models.js';

const STATUS_TEXTS = () => ({
  ACTIVATION_CANCELLED: t.UFGV(closedMsg),
  ACTIVE: t.SFRK(activeMsg),
  CLOSED_REPLACED: t.UFGV(closedMsg),
  CREATED: t.W59S(inDeliveryMsg),
  IN_ACTIVATION: t.W59S(inDeliveryMsg),
  SUSPENDED: t.PC3E(temporarilyClosedMsg),
  TERMINATED: t.UFGV(closedMsg),
});

const STATUS_COLORS = {
  ACTIVATION_CANCELLED: StatusColor.DARK_BLUE,
  ACTIVE: StatusColor.GREEN,
  CLOSED_REPLACED: StatusColor.DARK_BLUE,
  CREATED: StatusColor.YELLOW,
  IN_ACTIVATION: StatusColor.YELLOW,
  SUSPENDED: StatusColor.DARK_BLUE,
  TERMINATED: StatusColor.DARK_BLUE,
};

type DeviceType = 'accessories' | 'computers' | 'network equipment' | 'phone' | 'tablet';
const DEVICE_TYPE_TEXTS = () => ({
  accessories: t.O0EZ('Accessory'),
  computers: t.IHS4('Computer'),
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'network equipment': t.EG02('Network device'),
  phone: t.OWF1('Mobile phone'),
  tablet: t.ZFAS('Tablet'),
});

export const CATEGORY_TEXTS = () => ({
  broadband: t.SEYV(internetSubscriptionsMsg),
  device: t.JJTM(devicesMsg),
  service: t.MBG9(servicesMsg),
  voice: t.AR0B(voiceSubscriptionsMsg),
  contract: t.SW0J(contractsMsg),
  subscriptions: '',
  domain: '',
  elisadns: '',
  licenses: '',
});

export const getSubscriptionStatusColor = (status?: SubscriptionStatusType) => {
  if (!status) {
    return StatusColor.GREY;
  }
  return STATUS_COLORS[status.toString() as keyof typeof STATUS_COLORS] as StatusColor;
};

export const getSubscriptionStatusText = (status?: SubscriptionStatusType) => {
  if (!status) {
    return t.QWC8(unknownMsg);
  }
  return STATUS_TEXTS()[status.toString() as SubscriptionStatusType];
};

export const getSubscriptionStatusField = (subscriptionStatus: SubscriptionStatus) => (
  <Status color={subscriptionStatus.color} textAfterBadge={subscriptionStatus.text} />
);

export const getSubscriptionStatusFieldCL = (
  subscriptionStatus: SubscriptionStatus,
  label: string
): CLDescriptionItem => ({
  title: label,
  description: getSubscriptionStatusField(subscriptionStatus),
});

export const getDeviceTypeText = (subscriptionSubType: string) => {
  return DEVICE_TYPE_TEXTS()[subscriptionSubType.toLowerCase() as DeviceType] || '';
};

export const getExpiringEppDeviceSubscriptions = (
  subscriptions: Subscription[],
  subscriptionActions?: SubscriptionAction[]
): Subscription[] => {
  return subscriptions.filter(
    sub =>
      isEppDeviceSubscriptionExpiring(sub) &&
      subscriptionActions?.findIndex(subscriptionAction => subscriptionAction.subscriptionId === sub.subscriptionId) ===
        -1
  );
};
